import { useCoachQuery } from '@/features/home/queries';
import { usePlayerFilters } from '@/features/player/context/player-filters-context';
import { usePlayerSort } from '@/features/player/context/player-sort-context';
import { useListPlayersQuery } from '@/features/player/queries';
import { playerKeys } from '@/features/player/queries/query-keys';
import { lastActiveSecondsByIndex } from '@/features/player/utils/helpers';
import { useSessionStore } from '@/stores';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/shallow';

export const useListPlayer = (options?: { limit?: number }) => {
  const { limit = 50 } = options ?? {};
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const queryClient = useQueryClient();

  const user = useSessionStore(useShallow(s => s.user));
  const { key: sort, order, reset: resetSort } = usePlayerSort();
  const { data: coach } = useCoachQuery(user?.uid);
  const academyId = coach?.academy_id ?? '';
  const { level, lastActive } = usePlayerFilters();

  const lastActiveSeconds = lastActiveSecondsByIndex(lastActive);

  useEffect(() => {
    return () => {
      resetSort();
    };
  }, [resetSort, user?.uid]);

  const {
    data: paginatedPlayers,
    isFetching,
    isLoading,
    refetch,
    isRefetching,
  } = useListPlayersQuery({
    search: searchText,
    sort: sort
      ? (`${sort}${order.charAt(0).toUpperCase()}${order.slice(1)}` as player.SortBy)
      : undefined,
    pagination: true,
    academyId,
    filterInactiveFor: lastActiveSeconds,
    levelFilter: level,
    resultsPerPage: limit,
    expectedPage: page,
  });
  const listPlayer = paginatedPlayers?.data ?? [];
  const paginatedMeta = paginatedPlayers?.meta;

  const onRefetch = () => {
    queryClient.invalidateQueries({ queryKey: [...playerKeys.all, 'profile'] });
    refetch();
  };

  return {
    listPlayer,
    paginatedMeta,
    isFetching,
    isLoading,
    refetch: onRefetch,
    isRefetching,
    searchText,
    sort,
    order,
    level,
    lastActive,
    page,
    onChangePage: setPage,
    onChangeSearchText: setSearchText,
  };
};

export type UseListPlayerReturnType = ReturnType<typeof useListPlayer>;
